import { fetchMotorStatus } from '@/remote/dashboard/motor-status';
import { QUERY_KEYS } from '@/remote/query-key';
import type { MotorStatus } from '@/types/site';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseSiteMotorStatusOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number; // React Query v5에서 cacheTime → gcTime
  refetchInterval?: number;
}

interface UseSiteMotorStatusReturn {
  data: MotorStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  actions: {
    refetch: () => void;
    invalidate: () => void;
  };
}

export const useSiteMotorStatus = (
  siteId: string,
  options: UseSiteMotorStatusOptions = {}
): UseSiteMotorStatusReturn => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 1000 * 30, // 🚀 30초 - 실시간성 중요
    gcTime = 1000 * 60 * 5, // 🚀 5분 - 메모리 절약
    refetchInterval = 1000 * 30, // 🚀 30초마다 자동 갱신
  } = options;

  const query = useQuery<MotorStatus>({
    queryKey: QUERY_KEYS.dashboard.motorStatus(siteId),
    queryFn: () => fetchMotorStatus(siteId),
    enabled: enabled && !!siteId,
    staleTime,
    gcTime,
    refetchInterval,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.dashboard.motorStatus(siteId),
    });
  }, [queryClient, siteId]);

  const { refetch } = query;

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    actions: {
      refetch,
      invalidate,
    },
  };
};
